import React from 'react';

const C = {
  bg:'#08090f', card:'#0d1117', inp:'#0f1420',
  border:'#1f2937', borderS:'#1a2035',
  primary:'#e5e7eb', secondary:'#6b7280', muted:'#374151',
  profit:'#16C784', loss:'#ef4444', accent:'#6366f1',
};

function SDiv({ label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, margin:'6px 0' }}>
      <div style={{ flex:1, height:1, background:C.borderS }}/>
      <span style={{ color:C.muted, fontSize:9, fontWeight:800, letterSpacing:2, textTransform:'uppercase' }}>{label}</span>
      <div style={{ flex:1, height:1, background:C.borderS }}/>
    </div>
  );
}

function SCard({ children, accent, style={} }) {
  return (
    <div style={{
      borderRadius:12, padding:'10px 12px', boxSizing:'border-box',
      background: accent ? `linear-gradient(135deg,${accent}12 0%,${C.card} 100%)` : C.card,
      border:`1px solid ${accent ? `${accent}28` : C.border}`, ...style,
    }}>
      {children}
    </div>
  );
}

function SHead({ emoji, title, color }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
      <div style={{ width:20, height:20, borderRadius:6, background:`${color||C.accent}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:11 }}>
        {emoji}
      </div>
      <span style={{ color:C.primary, fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:1 }}>{title}</span>
    </div>
  );
}

function MC({ label, value, color }) {
  return (
    <div style={{ padding:'5px 8px', borderRadius:8, background:color?`${color}10`:C.inp, border:`1px solid ${color?`${color}22`:C.border}` }}>
      <div style={{ color:C.secondary, fontSize:8, fontWeight:600, marginBottom:1 }}>{label}</div>
      <div style={{ color:color||C.primary, fontSize:11, fontWeight:800, lineHeight:1.2 }}>{value??'—'}</div>
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{ display:'inline-block', background:`${color}18`, color, border:`1px solid ${color}28`, borderRadius:20, padding:'2px 8px', fontSize:9, fontWeight:700, marginRight:3, marginBottom:3 }}>
      {label}
    </span>
  );
}

function MarketRegimeShareCard({ snap, cardRef }) {
  if (!snap) return null;

  const { summary={}, signalCombos=[], periodLabel='Period' } = snap;
  const { hitRate=0, conflictDays=0, bestWindow='—', daysAnalyzed=0 } = summary;

  const ACCENT = '#0ea5e9';
  const hitCol = hitRate>=70?C.profit:hitRate>=50?'#f59e0b':C.loss;
  const dateStr = new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
  const combos = (signalCombos||[]).slice(0,6);
  const maxHitRate = combos.length ? Math.max(...combos.map(c=>c.hitRate||0),1) : 1;
  const circumference = 2*Math.PI*21;
  const dash = (hitRate/100)*circumference;

  const leftCombos = combos.slice(0, Math.ceil(combos.length/2));
  const rightCombos = combos.slice(Math.ceil(combos.length/2));

  return (
    <div
      ref={cardRef}
      style={{
        width:1080, height:1350, background:C.bg, overflow:'hidden', position:'relative',
        fontFamily:"'Segoe UI',system-ui,-apple-system,sans-serif",
        display:'flex', flexDirection:'column', padding:28, gap:8, boxSizing:'border-box',
      }}
    >
      {/* Ambient glow */}
      <div style={{ position:'absolute', top:-80, right:-80, width:220, height:220, borderRadius:'50%', background:`radial-gradient(circle,${ACCENT}0e 0%,transparent 65%)`, pointerEvents:'none' }}/>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#22c55e30,#22c55e10)', border:'1px solid #22c55e35', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ color:'#22c55e', fontSize:14, fontWeight:900 }}>T</span>
          </div>
          <span style={{ color:'#fff', fontSize:16, fontWeight:800 }}>TradeReflex</span>
        </div>
        <Pill label={periodLabel} color={ACCENT}/>
        <span style={{ color:C.muted, fontSize:11 }}>{dateStr}</span>
      </div>

      {/* Hero */}
      <SCard accent={hitCol}>
        <div style={{ display:'flex', alignItems:'center', gap:18 }}>
          <div style={{ position:'relative', width:90, height:90, flexShrink:0 }}>
            <svg viewBox="0 0 52 52" width="90" height="90" style={{ transform:'rotate(-90deg)' }}>
              <circle cx="26" cy="26" r="21" fill="none" strokeWidth="5" stroke={C.border}/>
              <circle cx="26" cy="26" r="21" fill="none" strokeWidth="5" stroke={hitCol} strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"/>
            </svg>
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:hitCol, fontSize:22, fontWeight:900, lineHeight:1 }}>{hitRate}%</span>
              <span style={{ color:C.secondary, fontSize:8 }}>HIT RATE</span>
            </div>
          </div>
          <div style={{ flex:1 }}>
            <p style={{ color:C.secondary, fontSize:10, margin:'0 0 2px', textTransform:'uppercase', letterSpacing:'2px', fontWeight:700 }}>Market Regime Analysis</p>
            <p style={{ color:'#fff', fontSize:24, fontWeight:900, margin:'0 0 6px', lineHeight:1.1 }}>Signal Accuracy Report</p>
            <p style={{ color:C.secondary, fontSize:12, margin:0 }}>{daysAnalyzed} days analysed · Best window: <span style={{ color:ACCENT, fontWeight:700 }}>{bestWindow}</span></p>
          </div>
        </div>
      </SCard>

      {/* 4 stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
        <MC label="Hit Rate"      value={`${hitRate}%`}      color={hitCol}/>
        <MC label="Conflict Days" value={String(conflictDays)} color="#f59e0b"/>
        <MC label="Best Window"   value={bestWindow}           color={ACCENT}/>
        <MC label="Days Analysed" value={String(daysAnalyzed)} color="#8b5cf6"/>
      </div>

      <SDiv label="Signal Reliability"/>

      {combos.length>0 && (
        <div style={{ display:'flex', gap:8, flex:1 }}>
          {/* Left column */}
          <SCard style={{ flex:1 }}>
            <SHead emoji="📊" title="Signal Combos" color={ACCENT}/>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {leftCombos.map((combo,i)=>{
                const barW = ((combo.hitRate||0)/maxHitRate)*100;
                const col = combo.color||(combo.hitRate>=70?C.profit:combo.hitRate>=50?'#f59e0b':C.loss);
                return (
                  <div key={i}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                      <span style={{ color:C.primary, fontSize:11, fontWeight:600 }}>{combo.regime}</span>
                      <span style={{ color:col, fontSize:11, fontWeight:800 }}>{combo.hitRate}% · {combo.days}d</span>
                    </div>
                    <div style={{ height:5, background:C.border, borderRadius:3, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${barW}%`, background:`linear-gradient(90deg,${col}60,${col})`, borderRadius:3 }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </SCard>

          {/* Right column */}
          {rightCombos.length>0 && (
            <SCard style={{ flex:1 }}>
              <SHead emoji="📈" title="More Combos" color="#8b5cf6"/>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {rightCombos.map((combo,i)=>{
                  const barW = ((combo.hitRate||0)/maxHitRate)*100;
                  const col = combo.color||(combo.hitRate>=70?C.profit:combo.hitRate>=50?'#f59e0b':C.loss);
                  return (
                    <div key={i}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ color:C.primary, fontSize:11, fontWeight:600 }}>{combo.regime}</span>
                        <span style={{ color:col, fontSize:11, fontWeight:800 }}>{combo.hitRate}% · {combo.days}d</span>
                      </div>
                      <div style={{ height:5, background:C.border, borderRadius:3, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${barW}%`, background:`linear-gradient(90deg,${col}60,${col})`, borderRadius:3 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </SCard>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:8, borderTop:`1px solid ${C.borderS}`, flexShrink:0, marginTop:'auto' }}>
        <span style={{ color:C.muted, fontSize:10 }}>Know your edge · TradeReflex</span>
        <span style={{ color:C.secondary, fontSize:10, fontWeight:700 }}>tradereflex.in</span>
      </div>
    </div>
  );
}

export default MarketRegimeShareCard;
